<template>
	<sk-dialog
		persistent
		:model-value="modelValue"
		:title="$t('billing.activity-report')"
		:icon="$i('chart-mixed')"
		@update:model-value="updateModelValue"
	>
		<v-form :id="FORM_ID" class="w-100" @submit.prevent="generateReport" :disabled="billingActivityReportLoading">
			<v-row>
				<v-col>
					<sk-select
						:items="startPeriodItems"
						:disabled="billingActivityReportLoading"
						:clearable="!billingActivityReportLoading"
						v-model="startPeriodField.value.value"
						:error="startPeriodField.errors.value[0]"
						attach
						:label="$t('billing.start-date')"
						:placeholder="$t('billing.start-date')"
					/>
				</v-col>
				<v-col>
					<sk-select
						:items="endPeriodItems"
						:disabled="billingActivityReportLoading"
						:clearable="!billingActivityReportLoading"
						v-model="endPeriodField.value.value"
						attach
						:error="endPeriodField.errors.value[0]"
						:label="$t('billing.end-date')"
						:placeholder="$t('billing.end-date')"
					/>
				</v-col>
			</v-row>

			<sk-select
				class="mt-3"
				multiple
				:disabled="billingActivityReportLoading"
				:clearable="!billingActivityReportLoading"
				v-model="typesField.value.value"
				attach
				:error="typesField.errors.value[0]"
				:items="typesItems"
				:label="$t('billing.report-type')"
				:placeholder="$t('billing.report-type')"
			/>
		</v-form>
		<template #footer="{ close }">
			<cancel-btn @click="close" />
			<sk-primary-btn type="submit" :form="FORM_ID" :loading="billingActivityReportLoading" :text="$t('commons.cta.generate')" />
		</template>
	</sk-dialog>
</template>

<style scoped lang="scss"></style>

<script setup lang="ts">
import { saveAs } from 'file-saver';
import kebabCase from 'lodash/kebabCase';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { finalize, tap } from 'rxjs/operators';
import { useI18n } from 'vue-i18n';
import { VCol, VForm, VRow } from 'vuetify/components';

import { SkDialog, SkPrimaryBtn, SkSelect } from '@silae/components';
import { useEventPropagation, useSubscriptor } from '@silae/composables';
import { useLoading, useNotification } from '~/composables';
import { MimeType } from '~/domain/enums';
import { generateBillingReport } from '~/services';
import { useAuthStore } from '~/stores';

import { CancelBtn } from '../../shared/buttons';
import { useBillingActivityReportForm } from './composables';

const FORM_ID = 'BILLING_ACTIVITY_REPORT_FORM';
const LOADING_FLAG_ACTIVITY_REPORT = 'billingActivityReportLoading';

/** Props and emits */
defineProps<{ modelValue: boolean }>();
defineEmits(['update:model-value']);

/** Composables and computed */
const { trigger } = useSubscriptor();
const { t } = useI18n();
const authStore = useAuthStore();
const { userPartnerLabel } = storeToRefs(authStore);
const { updateModelValue } = useEventPropagation(['update:model-value']);
const { billingActivityReportLoading, startLoading, stopLoading } = useLoading([LOADING_FLAG_ACTIVITY_REPORT]);
const { notifyResult } = useNotification();

const { handleSubmit, resetForm, startPeriodField, startPeriodItems, endPeriodField, endPeriodItems, typesField, typesItems } =
	useBillingActivityReportForm();

/** Functions used in comp */
const generateReport = handleSubmit(({ startPeriod, endPeriod, types }) => {
	startLoading(LOADING_FLAG_ACTIVITY_REPORT);
	trigger(
		generateBillingReport(startPeriod, endPeriod, types).pipe(
			notifyResult(t('notifications.success.action'), t('notifications.error.action')),
			tap(fileBits => {
				const now = DateTime.now();
				const formattedNow = now.toISODate();
				const file = new File([fileBits], `${kebabCase(userPartnerLabel.value)}_${formattedNow}_${now.toMillis()}`, {
					type: MimeType.ZIP
				});
				saveAs(file);
			}),
			finalize(() => {
				stopLoading(LOADING_FLAG_ACTIVITY_REPORT);
				// reset form
				resetForm();
				// and close dialog
				updateModelValue(false);
			})
		)
	);
});
</script>
